import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { type OaDealStatus, oaDealStatus } from '@openx/types/deal';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealsUsedByAudiences } from '../../../../../hooks/useDealsUsedByAudiences';
import { DealItem } from './DealItem/DealItem';

const isOaDealStatus = (input?: string | null): input is OaDealStatus => {
  const values = Object.values(oaDealStatus).map(String);

  return !!input && values.includes(input);
};

export function DealIds({ audienceId }: { audienceId: string }) {
  const { t } = useTranslation();

  const { error, loading, deals } = useDealsUsedByAudiences([audienceId]);

  const dealsMap = useMemo(
    () =>
      deals.map(deal => ({
        id: deal.id,
        dealId: deal.id,
        externalDealId: deal.deal_id ?? '',
        name: deal.name ?? '',
        status: isOaDealStatus(deal.status) ? deal.status : oaDealStatus.ERROR,
      })),
    [deals]
  );

  return (
    <FormSection title={t('Deal IDs')} divider description={t('This audience is used in the following deals')}>
      {loading && <Loader />}
      {!loading && (
        <>
          {error && <Alert severity="error">{t('Failed to fetch deals')}</Alert>}
          {deals.length === 0 ? (
            <Alert severity="info">{t('No deals')}</Alert>
          ) : (
            <Stack spacing={2}>
              {dealsMap.map(({ name, status, id, dealId, externalDealId }) => (
                <DealItem key={id} name={name} status={status} dealId={dealId} externalDealId={externalDealId} />
              ))}
            </Stack>
          )}
        </>
      )}
    </FormSection>
  );
}
